import axiosClient from '../../../api/axiosClient.js';
import { fetchPageData } from '../../../api/utils.js';

export async function getTournaments() {
  return fetchPageData(`/api/tournaments/tournaments/`);
}

export const tournamentsQuery = () => {
  return {
    queryKey: ['tournamentsInstructorReviewSession'],
    queryFn: async () => getTournaments(),
    refetchOnWindowFocus: false
  };
};

export async function getTournamentsRounds({tournamentId}) {
  return fetchPageData(`/api/tournaments/rounds/?tournament_id=${tournamentId}`);
}

export const tournamentsRoundsQuery = ({tournamentId}) => {
  return {
    queryKey: ['tournamentsRounds', tournamentId],
    queryFn: async () => getTournamentsRounds({tournamentId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function getTournamentsMatchups({tournamentId}) {
  return fetchPageData(`/api/tournaments/matchups/?tournament_id=${tournamentId}`);
}

export const tournamentsMatchupsQuery = ({tournamentId}) => {
  return {
    queryKey: ['tournamentsMatchups', tournamentId],
    queryFn: async () => getTournamentsMatchups({tournamentId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function updateTournamentsMatchupsBulk(matchups) {
  // matchups: [{ id, team1_id, team2_id, name? }, ...]
  const { data } = await axiosClient.put(`/api/tournaments/matchups/bulk/`, matchups);

  return data;
}

export async function getTournamentsTeams({tournamentId, roundId}) {
  return fetchPageData(`/api/tournaments/teams/?tournament_id=${tournamentId}${roundId ? `&round_id=${roundId}` : ''}`);
}

export const tournamentsTeamsQuery = ({tournamentId, roundId}) => {
  return {
    queryKey: ['tournamentsTeams', tournamentId, roundId],
    queryFn: async () => getTournamentsTeams({tournamentId, roundId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function updateTournamentRound({id, isActive, isFinished}) {
  const requestBody = {
    is_active: isActive
  };

  if (isFinished) {
    requestBody.is_finished = isFinished;
  }

  const { data } = await axiosClient.patch(`/api/tournaments/rounds/${id}/`, requestBody);

  return data;
}

export async function summarizeTournamentsScores({roundId}) {
  const { data } = await axiosClient.post(`/api/tournaments/tournaments/summarize-scores/`, {
    round_id: roundId
  });

  return data;
}

export async function getTournamentsTeamScores({tournamentId, roundNumber}) {
  return fetchPageData(`/api/tournaments/teamscores/?tournament_id=${tournamentId}${roundNumber ? `&round_number=${roundNumber}` : ''}`);
}

export const tournamentsTeamScoresQuery = ({tournamentId, roundNumber}) => {
  return {
    queryKey: ['tournamentsTeamScores', tournamentId, roundNumber],
    queryFn: async () => getTournamentsTeamScores({tournamentId, roundNumber}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function getTournamentsUserScores({tournamentId, roundNumber}) {
  return fetchPageData(`/api/tournaments/userscores/?tournament_id=${tournamentId}${roundNumber ? `&round_number=${roundNumber}` : ''}`);
}

// Returns the per-round user_total_points for the tournament's top
// performer (sum across rounds = their final score). Used by the
// championship celebration's third rotating frame.
export async function getTournamentsTopPerformer({tournamentId}) {
  return fetchPageData(`/api/tournaments/userscores/?top_performer&tournament_id=${tournamentId}`);
}

export async function configureNextRound({roundId, mathupsNumber}) {
  const { data } = await axiosClient.post(`/api/tournaments/tournaments/configure-next-round/`, {
    round_id: roundId,
    matchups_number: mathupsNumber
  });

  return data;
}

export async function moveStudentsBulk(operations) {
  // operations: [{ destination_team_id, student_ids }, ...]
  const { data } = await axiosClient.post(`/api/tournaments/tournaments/move-students/bulk/`, operations);

  return data;
}

async function getGame({id}) {
  const response = await axiosClient.get(`/api/games/games/${id}`);
  return response.data;
}

export const gameQuery = ({id}) => {
  return {
    queryKey: ['game', id],
    queryFn: async () => getGame({id}),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: !!id
  };
};

async function getGameDecisionsInsights({gameType, gameId}) {
  const response = await axiosClient.get(`/api/${gameType}/decisions/insights/${gameId}/`);
  return response?.data;
}

export const gameDecisionsQuery = ({gameType, gameId} = {}) => {
  return {
    queryKey: ['tournamentsTeamScores', gameType, gameId],
    queryFn: async () => getGameDecisionsInsights({gameType, gameId}),
    refetchOnWindowFocus: false,
    enabled: !!gameType && !!gameId
  };
};

export async function getGamesTransaction({id}) {
  const response = await axiosClient.get(`/api/games/transactions/${id}/`);
  return response;
}

export const gamesTransactionQuery = ({id} = {}) => {
  return {
    queryKey: ['tournamentGamesTransaction', id],
    queryFn: async () => getGamesTransaction({id}),
    refetchOnWindowFocus: false,
    enabled: !!id
  };
};

export async function getItems() {
  return fetchPageData(`/api/games/items/`);
}

export const itemsQuery = () => {
  return {
    queryKey: ['itemsInstructorReviewSession'],
    queryFn: async () => getItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getPrinciplesDecisions() {
  return fetchPageData('/api/principles/decisions/');
}

export const principlesDecisionsQuery = () => {
  return {
    queryKey: [`principlesDecisionsInstructorReviewSession`],
    queryFn: async () => getPrinciplesDecisions(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};